import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useBarcode } from 'react-barcodes';
import moment from 'moment';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import SvgIcon from '@material-ui/core/SvgIcon';

import useStore from '@hooks/useStore';
import CopyToClipboard from '@components/CopyToClipboard';

import useStyles from './styles';
import { ReactComponent as CopyIcon } from '@assets/images/icons/copy.svg';

import ExpirationDateIcon from '@yaydoo/react-components/lib/icons/ExpirationDate';

import OysterButton from '@components/common/Button';
import { paymentsInfoLink } from '@configs/variables';
import { PaymentMethod } from '@store/checkout';
import handleRedirect from '@utils/handleRedirect';

const CashReference: FC = () => {
  const { t } = useTranslation();
  const {
    kushki,
    checkout,
    links: {
      coBrandingConfig: { primaryColor, secondaryColor },
    },
  } = useStore();
  const css = useStyles({ primaryColor, secondaryColor });
  const { inputRef } = useBarcode({
    value: kushki.cashReferenceNumber,
    options: {
      background: 'transparent',
      displayValue: false,
      height: 55,
    },
  });

  const expirationDate = moment().add(3, 'days').format('YYYY/MM/DD');

  const redirect = () => {
    const payload = {
      cash_reference: kushki.cashReferenceNumber,
      barcode_url: kushki.barcodeUrl,
    };
    handleRedirect(payload);
  };

  const successHandler = () => {
    checkout.shouldAutoRedirect && redirect();
  };

  const onClickPayWithCard = () => {
    checkout.setPaymentMethod(PaymentMethod.card);
    checkout.resetSuccessStatus();
  };

  useEffect(() => {
    if (checkout.success.cashPayment) return successHandler();
  }, [checkout.success.cashPayment]);

  return (
    <>
      <Box className={css.barcodeContainer}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography className={css.referenceNumberTitle}>
              {t('checkoutScreen.payment.cashReference.referenceNumber')}
            </Typography>
            <Typography className={css.referenceNumber}>{kushki.cashReferenceNumber}</Typography>
          </Box>
          <Box>
            <IconButton className={css.copyIcon}>
              <CopyToClipboard
                tooltipText={t('checkoutScreen.payment.cashReference.copied')}
                text={kushki.cashReferenceNumber}
              >
                <SvgIcon component={CopyIcon} />
              </CopyToClipboard>
            </IconButton>
          </Box>
        </Box>

        <svg data-testid="svg-barcode" ref={inputRef} className={css.barcode} />

        <Box className={css.expirationDateMessage}>
          <ExpirationDateIcon className={css.expirationDateIcon} />
          <span>{t('checkoutScreen.payment.cashReference.expirationDateMessage')}</span>
        </Box>
      </Box>

      <Box data-testid="cash-reference-instructions" className={css.instructions}>
        <Typography variant="body2">
          {t('checkoutScreen.payment.cashReference.instructions.title')}
        </Typography>

        <ol>
          <li role="step">
            <Trans i18nKey="checkoutScreen.payment.cashReference.instructions.steps.0">
              <Link
                href={paymentsInfoLink}
                target="_blank"
                rel="noopener noreferrer"
                className={css.link}
              />
            </Trans>
          </li>
          <li role="step">{t('checkoutScreen.payment.cashReference.instructions.steps.1')}</li>
          <li role="step">{t('checkoutScreen.payment.cashReference.instructions.steps.2')}</li>
          <li role="step">{t('checkoutScreen.payment.cashReference.instructions.steps.3')}</li>
        </ol>

        <Typography variant="caption">
          <strong>{t('checkoutScreen.payment.cashReference.emailSentConfirmation')}</strong>
          <div>
            {t('checkoutScreen.payment.cashReference.expirationDate', {
              date: expirationDate,
            })}
          </div>
        </Typography>

        <OysterButton
          data-testid="pay-with-card-button"
          onClick={onClickPayWithCard}
          className={css.payWithCardButton}
          fullWidth
        >
          {t('checkoutScreen.payment.cashReference.payWithCardButton')}
        </OysterButton>
        {!checkout.shouldAutoRedirect && (
          <Button
            data-testid="continue-button"
            onClick={redirect}
            className={css.continueButton}
            fullWidth
            color="primary"
          >
            {t('checkoutScreen.payment.success.goBack')}
          </Button>
        )}
      </Box>
    </>
  );
};

export default observer(CashReference);
